import { useContext, useMemo, useState } from "react";
import * as Yup from "yup";
import { FormikProvider, getIn, useFormik } from "formik";
import { ISupplier, emptySupplier } from "../../../../types/supplier.types";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { updateSupplier } from "../../../../common/services/supplier.api";
import InputFormik from "../../../../common/components/form/InputFormik";
import { maskOfCpfOrCnpj, maskPhone } from "../../../../common/helpers/mask.helpers";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import TextareaFormik from "../../../renters/components/forms/TextareaFormik";
import { AuthContext } from '../../../../common/components/auth/authContext/AuthContext';

const isValidCpfOrCnpj = (value?: string) => {
  const digits = (value || "").replace(/\D/g, "");

  return digits.length === 11 || digits.length === 14;
};

const validationSchema = Yup.object({
  companyName: Yup.string().required('Razão Social obrigatória'),
  tradingName: Yup.string().required('Nome obrigatório'),
  cnpj: Yup.string()
    .required('CPF ou CNPJ obrigatório')
    .test('cpf-or-cnpj', 'CPF ou CNPJ inválido', isValidCpfOrCnpj),
  operationalContact: Yup.object().shape({
    name: Yup.string().required('Nome do contato operacional é obrigatório.'),
    phone: Yup.string().required('WhatsApp é obrigatório.'),
  }),
  workContact: Yup.object().shape({
    name: Yup.string().required('Nome do contato operacional é obrigatório.'),
    phone: Yup.string().required('WhatsApp é obrigatório.'),
  }),
})

type FormPropsCreateSupplier = {
  onSubmit?: (values) => unknown;
  onSave: (supplierId: string) => void
  data?: ISupplier
  formRequestErrors?: { message: string, path: string[] }[]
};

export default function FormEditSupplier(props: FormPropsCreateSupplier) {
  const authCtx = useContext(AuthContext);
  const userEmail = authCtx.auth?.user?.email;
  const { notifySuccess, notifyError } = useToastContext()

  const [theInitialData, setNewInitialData] = useState<ISupplier>(Object.assign(emptySupplier(), props.data as ISupplier, {
    observations: props.data?.observations || [{ text: '', date: new Date().toISOString(), author: '' }],
  }))

  const formik = useFormik({
    initialValues: theInitialData,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (data) => {
      data.observations.forEach(observation => {
        if (!observation.date) {
          observation.date = new Date().toISOString();
        }

        if (!observation.author) {  // Only set the author if it hasn't been set yet
          observation.author = userEmail || "";
        }
      });
      const dataToSave = { ...data }
      const result = await updateSupplier(dataToSave._id, dataToSave)
      if (!result.ok) {
        notifyError(result.message as string)
        return
      }
      notifySuccess("Atualizado com sucesso")
      setNewInitialData(result.data)
      props.onSave && props.onSave(data._id)
    }
  })

  const observations = getIn(formik.values, 'observations')
  const currentObservation = useMemo(() => {
    const total = observations.filter(({ date }) => !!date)
    return total.length
  }, [observations])

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <div className="grid grid-cols-2 gap-2">
          <InputFormik
            label='Razão Social:'
            name='companyName'
            className='col-span-2'
            required={true}
          />
          <InputFormik
            label='Nome fantasia:'
            name='tradingName'
            className='col-span-2'
            required={true}
          />
          <InputFormik
            label='E-mail:'
            name='supplierEmail'
            className='col-span-1'
            required={true}
          />
          <InputFormik
            label='CNPJ ou CPF:'
            name='cnpj'
            className='col-span-1'
            required={true}
            mask={maskOfCpfOrCnpj}
          />
          <InputFormik
            label='Nome do contato operacional:'
            name='operationalContact.name'
            className='col-span-1'
            required={true}
          />
          <InputFormik
            label='Telefone operacional:'
            name='operationalContact.phone'
            className='col-span-1'
            required={true}
            mask={maskPhone}
          />
          <InputFormik
            label='Nome do contato comercial:'
            name='workContact.name'
            className='col-span-1'
            required={true}
          />
          <InputFormik
            label='Telefone comercial:'
            name='workContact.phone'
            className='col-span-1'
            required={true}
            mask={maskPhone}
          />
        </div>
        <section>
          <div className="w-full dark:text-black bg-gray/40 rounded mb-4 p-3">
            <h2 className="font-semibold text-2xl mb-4">Observações</h2>
            <div>
              <TextareaFormik
                label={`Observação`}
                name={`observations.${currentObservation}.text`}
                className="min-h-64 w-full rounded shadow-md focus:border-white"
              />
            </div>

          </div>
        </section>
        <div className="flex justify-end col-span-2">
          <ButtonWithLoader isLoading={formik.isSubmitting} onClick={formik.handleSubmit}> Enviar </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  )
}
